<template>
  <div>
    <wc-page-title :title='title' :subtitle='subtitle' v-if='deposit'>
      <template v-slot:rightSide>
        <v-icon v-if='lockEdits' :color="lockEdits ? 'red' : 'grey'">mdi-lock</v-icon>
        <wc-burger-menu>
          <v-list-item @click='printDeposit'>
            <v-list-item-icon>
              <v-icon color='info'>mdi-printer</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Print</v-list-item-title>
          </v-list-item>
          <v-list-item @click='toggleLock' :disabled='deposit.reconciliation !== null'>
            <v-list-item-icon>
              <v-icon color='red'>mdi-lock</v-icon>
            </v-list-item-icon>
          <v-list-item-title v-if='lockEdits'>UN-Lock Deposit</v-list-item-title>
            <v-list-item-title v-else>Lock Deposit</v-list-item-title>
          </v-list-item>
        </wc-burger-menu>
      </template>
    </wc-page-title>
    <v-container v-if='deposit'>
      <v-alert v-if='deposit.reconciliation' small type='error' border='left'>Deposit has been reconciled and cannot be edited.</v-alert>
      <v-row>
        <v-col cols='12' md='9'>
          <wc-data-box title='Payments to include'
            showSearch
            v-model="search"
          >

            <v-data-table
              :headers='headers'
              :items='payments'
              :search='search'
              :items-per-page='-1'
              hide-default-footer
              sort-by='date_paid'
            >
            <template v-slot:item='{ item }'>
              <deposit-line
                :payment='item'
                :deposit='deposit'
                :lockEdits='lockEdits'
                @updatePayment='updatePayment'
              ></deposit-line>
            </template>
            </v-data-table>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='3'>
          <wc-data-box
            :title='toolbarData.title'
            :titleIcon='toolbarData.icon'
            :titleIconColor='toolbarData.iconColor'
            :showEdit='!lockEdits'
            @edit='editDetails'
            v-if='deposit'
          >
            <wc-two-column-table :items='details'></wc-two-column-table>
            <v-card-text class='text-center display-2'>
              {{ total | decMoney }}
            </v-card-text>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>

    <wc-dialog
      :title='toolbarData.title'
      :titleIcon='toolbarData.titleIcon'
      :showDialog='showDialog'
      width='350'
      :disableButtons='!isFormValid'
      :loadingButtons='isSavingDetails'
      @cancel='showDialog = false'
      @save='updateDetails'
    >
      <v-form v-model='isFormValid'>
        <wc-datepicker
          label='Deposit date'
          v-model='depositCopy.date'
          required
        ></wc-datepicker>
        <v-select
          :items='accounts'
          item-value='id'
          item-text='name'
          label='Account'
          v-model='depositCopy.account'
        ></v-select>
      </v-form>
    </wc-dialog>
    <deposit-tracker
      v-if='deposit' 
      :deposit='deposit'
      ref='actionTracker'
    ></deposit-tracker>
  </div>
</template>

<script>
import DepositTracker from '@/components/trackers/DepositTracker';
import accountingAPI from '@/api/accounting';
import trackerAPI from '@/api/tracker';
import miscServices from '@/services/misc';
import WCBurgerMenu from '@/components/nav/WCBurgerMenu';
import DepositLine from './DepositLine';
import _ from 'lodash';
import moment from 'moment';

export default {
  name: 'deposit-detail',
  data: function () {
    return {
      isLoading: true,
      showDialog: false,
      isSavingDetails: false,
      isFormValid: false,
      deposit: null,
      depositCopy: {},
      payments: [],
      accounts: [],
      search: ''
    }
  },
  computed: {
    headers () {
      return [ 
        { text: 'Date', value: 'date_paid', align: 'left', class: 'overline' },
        { text: 'Invoice', value: 'invoice_obj_number', align: 'center', class: 'overline' },
        { text: 'Payment Method', value: 'payment_method_display', align: 'left', class: 'overline' },
        { text: 'Amount', value: 'amount', align: 'end', class: 'overline' },
        { text: '' }, // completes the line over the checkbox (the breakline does not extend to the right border otherwise)
        { text: '', value: 'invoice_bill_to', align: ' d-none', sortable: false },  // makes this field searchable (though not displayed)
        { text: '', value: 'short_date_paid', align: ' d-none', sortable: false }   // makes this field searchable (though not displayed)
      ]
    },
    details () {
      if (this.deposit) {
        return [
          { text: 'Date of Deposit', value: this.$options.filters.shortDate(this.deposit.date) },
          { text: 'Account', value: this.deposit.account_obj.name },
          { text: '# of Items', value: this.included.length },
          { text: 'Deposit Total:', value: '' }
        ]
      }
      return []
    },
    lockEdits () {
      return this.deposit.reconciliation !== null || this.deposit.is_locked || this.deposit.archived
    },
    toolbarData () {
      return this.$store.state.icons.dataBox.details
    },
    title () {
      if (this.deposit) {
        return 'DEPOSIT - ' + this.deposit.account_obj.name + ' ACCOUNT'
      }
      return ''
    },
    subtitle () {
      if (this.deposit) {
        return this.$options.filters.longDate(this.deposit.date) 
      }
      return ''
    },
    included () {
      return this.payments.filter(p => p.deposit !== null)
    },
    total () {
      return _.sumBy(this.included, 'amount')
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    async getData () {
      this.isLoading = true
      this.deposit = await accountingAPI.getDeposit(this.$route.params.id)
      this.accounts = await accountingAPI.getAccounts()
      let tmp = await accountingAPI.getUndepositedPayments()
      tmp = tmp.concat(await accountingAPI.getPaymentsForDeposit(this.$route.params.id))
      this.payments = _.orderBy(tmp, 'id')
      this.payments.forEach(p => p.short_date_paid = this.$options.filters.shortDate(p.date_paid))
      // the above line is the hack way to make the short date filtered field searchable.  Can remove if it slows the system down.
      this.isLoading = false
    },
    editDetails () {
      this.depositCopy = _.cloneDeep(this.deposit)
      this.showDialog = true
    },
    async updateDetails () {
      this.isSavingDetails = true
      this.deposit = await accountingAPI.updateDeposit(this.depositCopy.id, this.depositCopy)
      this.showDialog = false
      this.isSavingDetails = false
    },
    updatePayment (p) {
      this.payments = miscServices.replaceObject(this.payments, p)
      this.updateDepositTotal();
    },
    updateDepositTotal () {
      this.deposit.amount = _.round(this.total, 2)
      accountingAPI.updateDeposit(this.deposit.id, this.deposit)
    },
    async toggleLock () {
      this.deposit.is_locked = !this.deposit.is_locked;

      let description = '';
      description = this.deposit.is_locked ? 'Deposit locked.' : 'Deposit UN-locked';
     
      const tracker = {
          'tracker_data': {
            'description': description,
            'taken_by': this.$store.state.auth.profile.id,
            'action_date': moment().format('YYYY-MM-DD').toString(),
          },
          'deposit': this.deposit.id
      }
      const res = await trackerAPI.createDepositTracker(tracker);
      this.$refs.actionTracker.pushItem(res);
      accountingAPI.updateDeposit(this.deposit.id, this.deposit);
    },
    printDeposit () {
      const link = document.createElement('a');
      const newTabURL = this.$router.resolve({ name: 'DepositPrintout', params: { id: this.deposit.id } });
      link.target = '_blank';
      link.href = newTabURL.href;
      link.click();
    }
  },
  components: {
    'deposit-line': DepositLine,
    'wc-burger-menu': WCBurgerMenu,
    'deposit-tracker': DepositTracker
  }
}
</script>

